import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type { Word } from '@/types'

interface ApiResponse<T> {
  success?: boolean
  message?: string
  data?: T
}

const ATTRIBUTE_KEYS = [
  'transcription',
  'translation',
  'part_of_speech',
  'type',
  'example_sentence',
  'synonym',
  'antonym',
  'note',
] as const

export interface WordCreator {
  id: number
  surname?: string | null
  first_name?: string | null
  email?: string
}

export interface WordDictionaryRef {
  id: number
  name: string
}

export interface WordWithTags extends Word {
  tags?: Array<{ id: number; name: string; slug?: string }>
  dictionary_id?: number
  creator?: WordCreator | null
  users_count?: number
  part_of_speech?: string
  type?: string
  example_sentence?: string
  synonym?: string
  antonym?: string
  note?: string
  dictionaries?: WordDictionaryRef[]
  dictionaries_count?: number
}

interface WordAttribute {
  key: string
  value: string
}

function getAttr(attributes: WordAttribute[] | undefined, key: string): string | undefined {
  const a = attributes?.find((x: WordAttribute) => x.key === key)
  return a?.value
}

export interface ApiWord {
  id?: number
  word?: string
  text?: string
  attributes?: WordAttribute[]
  creator?: WordCreator | null
  users_count?: number
  dictionaries?: WordDictionaryRef[]
  dictionaries_count?: number
  [key: string]: unknown
}

export function mapApiWord(apiWord: ApiWord): WordWithTags {
  const mapped: Partial<WordWithTags> = { ...apiWord } as Partial<WordWithTags>
  if (apiWord.text && !apiWord.word) mapped.word = apiWord.text
  if (Array.isArray(apiWord.attributes)) {
    mapped.transcription = getAttr(apiWord.attributes, 'transcription')
    mapped.translation = getAttr(apiWord.attributes, 'translation')
    mapped.part_of_speech = getAttr(apiWord.attributes, 'part_of_speech')
    mapped.type = getAttr(apiWord.attributes, 'type')
    mapped.example_sentence = getAttr(apiWord.attributes, 'example_sentence')
    mapped.synonym = getAttr(apiWord.attributes, 'synonym')
    mapped.antonym = getAttr(apiWord.attributes, 'antonym')
    mapped.note = getAttr(apiWord.attributes, 'note')
  }
  if (apiWord.creator) {
    mapped.creator = {
      id: apiWord.creator.id,
      surname: apiWord.creator.surname,
      first_name: apiWord.creator.first_name,
      email: apiWord.creator.email,
    }
  }
  if (typeof apiWord.users_count === 'number') mapped.users_count = apiWord.users_count
  if (Array.isArray(apiWord.dictionaries)) mapped.dictionaries = apiWord.dictionaries
  if (typeof apiWord.dictionaries_count === 'number') mapped.dictionaries_count = apiWord.dictionaries_count
  return mapped as WordWithTags
}

export const wordsApi = {
  getWords: async (params?: {
    page?: number
    per_page?: number
    search?: string
    exact?: boolean
    dictionary_id?: number
    not_in_any_dictionary?: boolean
    without_translation?: boolean
    without_transcription?: boolean
    without_audio?: boolean
    is_imported?: boolean
    tags?: number[]
    part_of_speech?: string
    type?: string
    order_by?: string
    order_direction?: 'asc' | 'desc'
    sort?: string
    order?: 'asc' | 'desc' | string
  }): Promise<{
    data: WordWithTags[]
    current_page?: number
    last_page?: number
    per_page?: number
    total?: number
  }> => {
    // Убеждаемся, что boolean параметры всегда отправляются (true или false)
    // Используем кастомный paramsSerializer, чтобы явно включить boolean значения
    interface QueryParams {
      page?: number
      per_page?: number
      search?: string
      exact?: boolean
      dictionary_id?: number
      not_in_any_dictionary?: boolean
      without_translation?: boolean
      without_transcription?: boolean
      without_audio?: boolean
      is_imported?: boolean
      tags?: number[]
      part_of_speech?: string
      type?: string
      order_by?: string
      order_direction?: 'asc' | 'desc'
      sort?: string
      order?: 'asc' | 'desc' | string
    }
    const queryParams: QueryParams = { ...params }
    if (queryParams.sort !== undefined) {
      queryParams.order_by = queryParams.order_by ?? queryParams.sort
      if (queryParams.order && (queryParams.order === 'asc' || queryParams.order === 'desc')) {
        queryParams.order_direction = queryParams.order_direction ?? queryParams.order as 'asc' | 'desc'
      }
    }
    delete queryParams.sort
    delete queryParams.order

    interface WordsResponse {
      data?: ApiWord[]
      meta?: {
        current_page?: number
        last_page?: number
        per_page?: number
        total?: number
      }
      current_page?: number
      last_page?: number
      per_page?: number
      total?: number
      message?: string
    }
    const response = await apiClient.get<ApiWord[] | WordsResponse>(API_ENDPOINTS.WORDS, {
      params: queryParams,
      paramsSerializer: {
        indexes: null, // не использовать индексы для массивов
        serialize: (params: Record<string, unknown>) => {
          const searchParams = new URLSearchParams()
          Object.keys(params).forEach((key) => {
            const value = params[key]
            if (value !== undefined && value !== null) {
              // Для boolean всегда отправляем как строку "true" или "false"
              if (typeof value === 'boolean') {
                searchParams.append(key, value ? 'true' : 'false')
              } else if (Array.isArray(value)) {
                value.forEach((item) => searchParams.append(key, String(item)))
              } else {
                searchParams.append(key, String(value))
              }
            }
          })
          return searchParams.toString()
        },
      },
    })

    // Обрабатываем разные форматы ответа
    if (Array.isArray(response.data)) {
      return {
        data: response.data.map(mapApiWord),
      }
    }

    if (response.data?.data && Array.isArray(response.data.data)) {
      const raw = response.data
      const meta = raw.meta || {}
      return {
        data: (raw.data ?? []).map(mapApiWord),
        current_page: meta.current_page ?? raw.current_page ?? 1,
        last_page: meta.last_page ?? raw.last_page ?? 1,
        per_page: meta.per_page ?? raw.per_page ?? 20,
        total: meta.total ?? raw.total ?? 0,
      }
    }

    throw new Error(response.data?.message || 'Ошибка загрузки слов')
  },

  getWord: async (id: number): Promise<WordWithTags> => {
    interface WordResponse {
      data?: ApiWord
      id?: number
      message?: string
    }
    const response = await apiClient.get<WordResponse | ApiWord>(`${API_ENDPOINTS.WORDS}/${id}`)

    const data = response.data as WordResponse | ApiWord
    if (data && 'data' in data && data.data) {
      return mapApiWord(data.data as ApiWord)
    }

    if (data && ('id' in data || 'word' in data || 'text' in data)) {
      return mapApiWord(data as ApiWord)
    }

    throw new Error((data as WordResponse)?.message || 'Ошибка загрузки слова')
  },

  createWord: async (wordData: {
    word: string
    transcription?: string
    translation?: string
    part_of_speech?: string
    type?: string
    example_sentence?: string
    synonym?: string
    antonym?: string
    note?: string
    tag_ids?: number[]
  }): Promise<WordWithTags> => {
    const attributes: Array<{ key: string; value: string }> = []
    ATTRIBUTE_KEYS.forEach((key) => {
      const v = wordData[key as keyof typeof wordData]
      if (v != null && String(v).trim() !== '') {
        attributes.push({ key, value: String(v).trim() })
      }
    })
    if (attributes.length === 0) {
      attributes.push({ key: 'translation', value: '' })
    }

    interface CreateWordPayload {
      text: string
      attributes: Array<{ key: string; value: string }>
      tags?: number[]
    }
    const payload: CreateWordPayload = {
      text: wordData.word.trim(),
      attributes,
    }
    if (wordData.tag_ids?.length) {
      payload.tags = wordData.tag_ids
    }

    interface CreateWordResponse {
      data?: ApiWord
      id?: number
      message?: string
    }
    const response = await apiClient.post<CreateWordResponse | ApiWord>(
      API_ENDPOINTS.WORDS,
      payload
    )

    const data = response.data as CreateWordResponse | ApiWord
    if (data && 'data' in data && data.data) {
      return mapApiWord(data.data as ApiWord)
    }

    if (data && ('id' in data || 'word' in data || 'text' in data)) {
      return mapApiWord(data as ApiWord)
    }

    throw new Error((data as CreateWordResponse)?.message || 'Ошибка создания слова')
  },

  updateWord: async (id: number, wordData: {
    word?: string
    transcription?: string
    translation?: string
    part_of_speech?: string
    type?: string
    example_sentence?: string
    synonym?: string
    antonym?: string
    note?: string
    tag_ids?: number[]
  }): Promise<WordWithTags> => {
    const attributes: Array<{ key: string; value: string }> = []
    const hasAnyAttributeInput = ATTRIBUTE_KEYS.some((key) => {
      return wordData[key as keyof typeof wordData] !== undefined
    })
    ATTRIBUTE_KEYS.forEach((key) => {
      const v = wordData[key as keyof typeof wordData]
      if (v != null && String(v).trim() !== '') {
        attributes.push({ key, value: String(v).trim() })
      }
    })
    if (hasAnyAttributeInput && attributes.length === 0) {
      attributes.push({ key: 'translation', value: '' })
    }
    interface UpdateWordPayload {
      text?: string
      attributes?: Array<{ key: string; value: string }>
      tags?: number[]
    }
    const payload: UpdateWordPayload = {}
    if (wordData.word !== undefined) {
      payload.text = wordData.word.trim()
    }
    if (hasAnyAttributeInput) {
      payload.attributes = attributes
    }
    if (wordData.tag_ids !== undefined) {
      payload.tags = wordData.tag_ids
    }
    interface UpdateWordResponse {
      data?: ApiWord
      id?: number
      message?: string
    }
    const response = await apiClient.put<UpdateWordResponse | ApiWord>(
      `${API_ENDPOINTS.WORDS}/${id}`,
      payload
    )

    const data = response.data as UpdateWordResponse | ApiWord
    if (data && 'data' in data && data.data) {
      return mapApiWord(data.data as ApiWord)
    }

    if (data && ('id' in data || 'word' in data || 'text' in data)) {
      return mapApiWord(data as ApiWord)
    }

    throw new Error((data as UpdateWordResponse)?.message || 'Ошибка обновления слова')
  },

  deleteWord: async (id: number): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `${API_ENDPOINTS.WORDS}/${id}`
    )

    if (response.data?.success === false) {
      throw new Error(response.data.message || 'Ошибка удаления слова')
    }
  },
}
